import React, { useEffect, useState } from 'react';
import type { Branch } from '../../types/db';
import {
  fetchBranches,
  createBranch,
  updateBranch,
  deleteBranch,
} from '../../services/branchService';
import { Plus, Trash2, Building, Edit2, Loader2, Save, X } from 'lucide-react';

export function BranchManager() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ id: '', name: '', address: '', is_active: true });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = async () => {
    try {
      const data = await fetchBranches();
      setBranches(data);
    } catch (err: any) {
      /* console removed */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ id: '', name: '', address: '', is_active: true });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (branch: Branch) => {
    setEditingId(branch.id);
    setFormData({
      id: branch.id,
      name: branch.name,
      address: branch.address || '',
      is_active: branch.is_active !== false,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateBranch(editingId, {
          name: formData.name,
          address: formData.address,
          is_active: formData.is_active,
        });
      } else {
        await createBranch({ id: formData.id, name: formData.name, address: formData.address });
      }
      setIsFormOpen(false);
      loadData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Hapus cabang ini?')) return;
    try {
      await deleteBranch(id);
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center p-20">
        <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
      </div>
    );

  return (
    <div className="space-y-6 animate-fade-in pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase">Manajemen Cabang</h1>
          <p className="text-xs text-gray-500 font-medium">
            Aktifkan atau nonaktifkan cabang yang tutup tanpa menghapus data historisnya.
          </p>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-indigo-700 transition-all"
        >
          <Plus className="w-4 h-4" /> Tambah Cabang
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {branches.map((branch: Branch) => {
          const isActive = branch.is_active !== false;
          return (
            <div
              key={branch.id}
              className={`bg-white p-8 rounded-[2.5rem] border shadow-sm relative group hover:shadow-xl transition-all ${
                isActive ? 'border-gray-100' : 'border-red-100 bg-slate-50/70 opacity-80'
              }`}
            >
              <div className="flex items-start gap-5">
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-all ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white'
                      : 'bg-red-50 text-red-500'
                  }`}
                >
                  <Building className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-black text-xl text-gray-900 truncate">{branch.name}</h3>
                    {isActive ? (
                      <span className="bg-emerald-50 text-emerald-600 text-[9px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider">
                        Aktif
                      </span>
                    ) : (
                      <span className="bg-red-50 text-red-600 text-[9px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider">
                        Nonaktif
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
                    {branch.address || 'Alamat belum diset'}
                  </p>
                  <div className="text-[10px] text-indigo-600 mt-3 font-black uppercase tracking-widest">
                    ID: {branch.id}
                  </div>
                </div>
              </div>
              <div className="absolute top-6 right-6 flex gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleOpenEdit(branch)}
                  className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                  title="Edit Cabang"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(branch.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                  title="Hapus Cabang"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[3.5rem] w-full max-w-lg p-10 shadow-2xl animate-fade-in-up border border-gray-100 relative">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-8 right-8 text-gray-400 hover:text-gray-900 transition-colors"
            >
              <X />
            </button>
            <h3 className="font-black text-3xl text-gray-900 text-center mb-8">
              {editingId ? 'Edit Cabang' : 'Cabang Baru'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              {!editingId && (
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-1">
                    ID Cabang (Unik)
                  </label>
                  <input
                    className="w-full border-2 border-gray-50 p-4 rounded-2xl outline-none font-bold"
                    placeholder="DELTA_XYZ"
                    value={formData.id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        id: e.target.value.toUpperCase().replace(/\s/g, '_'),
                      })
                    }
                    required
                  />
                </div>
              )}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-1">
                  Nama Cabang
                </label>
                <input
                  className="w-full border-2 border-gray-50 p-4 rounded-2xl outline-none font-bold"
                  placeholder="Delta Jakarta"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-1">
                  Alamat
                </label>
                <textarea
                  className="w-full border-2 border-gray-50 p-4 rounded-2xl outline-none font-bold h-32 resize-none"
                  placeholder="Alamat lengkap..."
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              {editingId && (
                <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-2xl border border-gray-100">
                  <input
                    type="checkbox"
                    id="is_active"
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <label
                    htmlFor="is_active"
                    className="text-sm font-bold text-gray-700 select-none cursor-pointer"
                  >
                    Cabang Aktif (Tampilkan di Berbagai Menu Pilihan)
                  </label>
                </div>
              )}

              <button
                disabled={isSubmitting}
                className="w-full bg-indigo-600 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest shadow-xl flex justify-center items-center gap-3 hover:bg-indigo-700 transition-all mt-4"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin w-6 h-6" />
                ) : (
                  <Save className="w-6 h-6" />
                )}
                {editingId ? 'PERBARUI DATA' : 'AKTIFKAN CABANG'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
